import {
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Box,
	Tooltip,
	IconButton,
	useTheme,
	useMediaQuery
} from '@mui/material';
import {
	Dashboard as DashboardIcon,
	AdminPanelSettings as AdminIcon,
	Sensors as SensorsIcon,
	History as HistoryIcon,
	Code as CodeIcon,
	BugReport as BugReportIcon,
	ChevronLeft,
	ChevronRight
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/auth-context';

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 65;

const Sidebar = ({ open, onToggle }) => {
	const { user, isAdmin, hasCompanyAssignment } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	// Auto-collapse on mobile
	const isOpen = isMobile ? false : open;

	const isActive = (path) => location.pathname === path;

	// Determine which navigation items to show
	const showCustomerPortal = user && hasCompanyAssignment;
	const showAdminPortal = user && isAdmin;

	const navigationItems = [
		// Customer Portal sections
		...(showCustomerPortal ? [
			{
				text: 'Sensors',
				icon: <SensorsIcon />,
				path: '/sensors',
				show: true
			},
			{
				text: 'Realtime',
				icon: <DashboardIcon />,
				path: '/dashboard',
				show: true
			},
			{
				text: 'Historical',
				icon: <HistoryIcon />,
				path: '/historical',
				show: true
			},
			{
				text: 'Developer',
				icon: <CodeIcon />,
				path: '/developer',
				show: true
			},
			{
				text: 'Issues',
				icon: <BugReportIcon />,
				path: '/issues',
				show: true
			},
			/*{
				text: 'Sandbox',
				icon: <ScienceIcon />,
				path: '/sandbox',
				show: true
			}*/
		] : []),
		// Admin Portal
		{
			text: 'Admin Portal',
			icon: <AdminIcon />,
			path: '/admin-portal',
			show: showAdminPortal
		},
		{
			text: 'Issue Tracker',
			icon: <BugReportIcon />,
			path: '/admin-portal/issues',
			show: showAdminPortal
		}
	].filter(item => item.show);

	const handleNavigation = (path) => {
		navigate(path);
	};

	if (!user) return null;

	const drawerContent = (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
			{/* Toggle Button */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: isOpen ? 'flex-end' : 'center',
					px: isOpen ? 2 : 0,
					py: 2,
					minHeight: 56
				}}
			>
				<Tooltip 
					title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'} 
					placement="right"
					arrow
				>
					<IconButton
						onClick={onToggle}
						sx={{
							color: theme.palette.text.primary,
							'&:hover': {
								backgroundColor: theme.palette.action.hover
							}
						}}
					>
						{isOpen ? <ChevronLeft /> : <ChevronRight />}
					</IconButton>
				</Tooltip>
			</Box>

			{/* Navigation Items */}
			<List sx={{ flexGrow: 1, pt: 3, px: isOpen ? 1.5 : 0 }}>
				{navigationItems.map((item) => (
					<ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
						<Tooltip 
							title={item.text} 
							placement="right" 
							disableHoverListener={isOpen}
							arrow
						>
							<ListItemButton
								onClick={() => handleNavigation(item.path)}
								selected={isActive(item.path)}
								sx={{
									minHeight: 56,
									justifyContent: 'center',
									px: isOpen ? 2 : 0,
									py: 1.5,
									mx: isOpen ? 0 : 0,
									borderRadius: 3,
									mb: 0.5,
									'&.Mui-selected': {
										backgroundColor: theme.palette.mode === 'dark' 
											? 'rgba(255, 255, 255, 0.12)'
											: 'rgba(0, 0, 0, 0.08)',
										'& .MuiListItemIcon-root': {
											color: theme.palette.primary.main
										}
									},
									'&:hover': {
										backgroundColor: theme.palette.action.hover
									}
								}}
							>
								<ListItemIcon
									sx={{
										minWidth: isOpen ? 40 : '100%',
										mr: isOpen ? 2 : 0,
										ml: isOpen ? 0 : 0,
										justifyContent: 'center',
										display: 'flex',
										color: isActive(item.path)
											? theme.palette.primary.main
											: theme.palette.text.secondary,
										'& .MuiSvgIcon-root': {
											fontSize: '1.5rem'
										}
									}}
								>
									{item.icon}
								</ListItemIcon>
								<ListItemText
									primary={item.text}
									sx={{
										opacity: isOpen ? 1 : 0,
										display: isOpen ? 'block' : 'none'
									}}
								/>
							</ListItemButton>
						</Tooltip>
					</ListItem>
				))}
			</List>
		</Box>
	);

	return (
		<Drawer
			variant="permanent"
			open={isOpen}
			sx={{
				width: isOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH,
				flexShrink: 0,
				'& .MuiDrawer-paper': {
					width: isOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH,
					boxSizing: 'border-box',
					transition: theme.transitions.create('width', {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.enteringScreen
					}),
					overflowX: 'hidden',
					backgroundColor: theme.palette.background.paper,
					borderRight: `1px solid ${theme.palette.dividerSecondary}`,
					top: 64, // Height of the header
					height: 'calc(100vh - 64px)' // Full height minus header
				}
			}}
		>
			{drawerContent}
		</Drawer>
	);
};

export default Sidebar;
